import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { Observable, of, Subject, concat } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  tap,
} from "rxjs/operators";

import { SearchFunc } from "src/app/shared/utils/func";

import { ControlValueAccessorDirective } from "src/app/shared/directive";
import { GenerateUuid } from "src/app/shared/utils";

interface SearchTerm {
  term: string;
  items: any[];
}

@Component({
  selector: "app-field-input-ng-select",
  templateUrl: "./field-input-ng-select.component.html",
  styleUrls: ["./field-input-ng-select.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FieldInputNgSelectComponent),
      multi: true,
    },
  ],
})
export class FieldInputNgSelectComponent<
  T
> extends ControlValueAccessorDirective<T> {
  @Input() set search(fn: SearchFunc) {
    this.setSearchFn(fn);
  }
  @Input() container: string = "body";
  @Input() id: string = `field-${GenerateUuid()}`;
  @Input() displayLabel: string = "";
  @Input() displayField: string;
  @Input() valueField: string | null;
  @Input() groupField: string | null = null;
  @Input() clsLabel: string | null = "col-form-label";
  @Input() minLengthToSearch: number = 0;
  @Input() readOnly: boolean = false;
  @Input() rightText: string | null;
  @Input() placeholder: string | null = null;
  @Input() canAddNew: boolean = false;
   // tslint:disable-next-line: no-output-on-prefix
  @Output() onNoResults = new EventEmitter();
  @Output() onAddNew = new EventEmitter<string>();

  @Output() onChangeValue: EventEmitter<T> = new EventEmitter<T>();

  textSearch: string;
  recordLoading = false;
  suggestions$: Observable<T[]> = of([]);
  recordInput$ = new Subject<string>();

  private searchFn: SearchFunc;

  private setSearchFn(fn: SearchFunc): void {
    this.searchFn = fn;

    this.suggestions$ = concat(
      of([]), // default items
      this.recordInput$.pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        tap(() => (this.recordLoading = true)),
        //tap(console.log),
        switchMap((term) =>
          this.searchFn(term).pipe(
            catchError(() => of([])), // empty list on error
            tap(() => (this.recordLoading = false)),
            tap(obj => {
              // Para objetos que apenas tem uma key e precisam ser carregados
              // antes de setar o valor para o model
              if (obj.length === 1) {
                let value = obj[0];
                if (this.valueField) {
                  value = value[this.valueField]
                }
                super.writeValue(value);
                this.onChange(value);
              }
            }),
          )
        )
      )
    );
  }

  trackByFn(item: T) {
    return this.valueField ? item[this.valueField] : item["id"];
  }

  onChange(value: T): void {
    if (this.onChangeValue) {
      this.onChangeValue.emit(value);
    }
  }

  onModelChange($event): void {
    // Apenas para o angular registrar modificações via ([ngModel])
    // console.log($event);
  }

  onOpen($event) {
    if (this.minLengthToSearch > 0) return;

    this.recordInput$.next("");
  }

  writeValue(value: T): void {
    // Casos em que é necessário carregar o objeto da API por iniciar apenas com a chave
    if (!!this.searchFn && !!value && !(value instanceof Object) && !!this.suggestions$ && !!this.valueField) {
      // Aguarda o componente renderizar para setar o valor
      setTimeout(() => {
        this.recordInput$.next(`${value}`);
      }, 100);
    } else {
      super.writeValue(value);
    }
  }

  clear(): void {
    this.writeValue(null);
    this.onChange(null);
  }

  getTypeToSearchText() {
    return this.minLengthToSearch > 0
      ? `Digite ${this.minLengthToSearch} ou mais caracteres`
      : 'Nenhum registro encontrado';
  }

  onAddNewClick(searchTerm: string): void {
    this.onAddNew.emit(searchTerm);
  }

  onSearch(data: SearchTerm): void {
    if (data && data?.items && data.items.length == 0) {
      this.onNoResults.emit(true);
    } else {
      this.onNoResults.emit(false);
    }
  }
}
